import React, {Component} from 'react';
import {Marker, Polygon, Polyline} from 'react-google-maps';

import {Project} from '../../types';
import ProjectPopup from '../ProjectPopup';
import {loadGeoJSON} from '~utils/geojson';

import {ACTION_DOT_ACTIVE_RADIUS, ACTION_DOT_COLOR, ACTION_DOT_RADIUS, NAVY} from './constants';
import {Route} from "~services/RoutesService";

interface Props {
    project: Project | Route;
    hovered: boolean;
    visible: boolean;
    selectable: boolean;
    selected?: boolean;
    onSetSelected?: (event: React.MouseEvent) => void;
    onUnsetSelected?: (event: React.MouseEvent) => void;
    activeSettings?: {
        strokeColor?: string;
        strokeWeight?: number;
        fillColor?: string;
        fillOpacity?: number;
    },
    inactiveSettings?: {
        strokeColor?: string;
        strokeWeight?: number;
        fillColor?: string;
        fillOpacity?: number;
    }
}

interface State {
    polylines: any[];
    markers: any[];
    polygons: any[];
    multi_polygons: any[];
    selected: boolean;
}

class ProjectMarker extends Component<Props, State> {

    public props;
    public active = true;
    public icon;
    public markerProps;

    constructor(props) {
        super(props);

        this.props = props;
        this.state = {
            polylines: [],
            markers: [],
            polygons: [],
            multi_polygons: [],
            selected: false
        }
        const {project} = this.props;
        const mapFile = project?.kml ? project.kml : project.kmz;
        if (mapFile) {
            this.setPolylines(loadGeoJSON(mapFile));
        }
    }


    getActiveSettings = () => {
        const {activeSettings} = this.props;
        const defaultActiveSettings = {
            strokeColor: NAVY,
            strokeWeight: 2,
            fillColor: NAVY,
            fillOpacity: 0.3
        }
        return {...defaultActiveSettings, ...activeSettings};
    }

    getInactiveSettings = () => {
        const {inactiveSettings} = this.props;
        const defaultInactiveSettings = {
            strokeColor: ACTION_DOT_COLOR,
            strokeWeight: 2,
            fillColor: ACTION_DOT_COLOR,
            fillOpacity: 0.3
        }
        return {...defaultInactiveSettings, ...inactiveSettings};
    }

    setPolylines = (gjsonPromise) => {
        const polylines: any[] = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'LineString') {
                    const line: any[] = [];
                    f.geometry.coordinates.forEach(c => {
                        line.push({lng: c[0], lat: c[1]});
                    });
                    polylines.push(line);
                } else if (f.geometry.type == 'MultiPolygon') {
                    const paths: any[] = [];
                    f.geometry.coordinates.forEach(path => {
                        const pol: any[] = [];
                        path[0].forEach(c => {
                            pol.push({lng: c[0], lat: c[1]});
                        });
                        paths.push(pol);
                    });
                    this.setState({...this.state, multi_polygons: paths});
                } else if (f.geometry.type == 'MultiLineString') {
                    f.geometry.coordinates.forEach(path => {
                        const line: any[] = [];
                        path.forEach(c => {
                            line.push({lng: c[0], lat: c[1]});
                        });
                        polylines.push(line);
                    });
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'LineString') {
                            const line: any[] = [];
                            g.coordinates.forEach(c => {
                                line.push({lng: c[0], lat: c[1]});
                            });
                            polylines.push(line);
                        }
                    });
                }
            });
            this.setState({...this.state, polylines: polylines});
            this.setPolygons(gjsonPromise);

        })
    };

    setPolygons = (gjsonPromise) => {
        const polygons: any = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'Polygon') {
                    const pol: any = [];
                    f.geometry.coordinates[0].forEach(c => {
                        pol.push({lng: c[0], lat: c[1]});
                    });
                    polygons.push(pol);
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'Polygon') {
                            const pol: any = [];
                            g.coordinates[0].forEach(c => {
                                pol.push({lng: c[0], lat: c[1]});
                            });
                            polygons.push(pol);
                        }
                    });
                }
            });
            this.setState({...this.state, polygons: polygons});
            this.setMarkers(gjsonPromise);
        })
    }

    setMarkers = (gjsonPromise) => {
        const markers: any[] = [];
        gjsonPromise.then(gjson => {
            gjson.features.forEach(f => {
                if (f.geometry.type == 'Point') {
                    const coor = f.geometry.coordinates;
                    markers.push({lng: coor[0], lat: coor[1]});
                } else if (f.geometry.hasOwnProperty('geometries')) {
                    f.geometry.geometries.forEach(g => {
                        if (g.type == 'Point') {
                            const coor = g.coordinates;
                            markers.push({lng: coor[0], lat: coor[1]});
                        }
                    })
                }
            });
            if (markers.length == 0 && this.state.polylines && this.state.polylines[0] && this.props.selectable) {
                let firstLine = this.state.polylines[0][0];
                markers.push(firstLine);
            } else if (markers.length == 0 && this.state.polygons && this.state.polygons[0] && this.props.selectable) {
                let firstPolygon = this.state.polygons[0][0];
                markers.push(firstPolygon);
            }
            this.setState({...this.state, markers: markers});

        });
    }

    renderPolylines = (lines: any[], index: number) => {
        const {visible} = this.props;
        const options = this.active ? this.getActiveSettings() : this.getInactiveSettings();
        return (
            <Polyline
                key={index}
                onClick={() => this.onClick()}
                path={lines}
                visible={visible}
                options={options}
            />
        );
    };

    renderPolygons = (polygon, index: number) => {
        const {visible} = this.props;
        const options = this.active ? this.getActiveSettings() : this.getInactiveSettings();
        return (
            <Polygon
                key={index}
                onClick={() => this.onClick()}
                path={polygon}
                visible={visible}
                options={options}
            />
        )
    }

    renderMarkers = (marker: any, index: number) => {
        const first = index === 0;
        const {visible, selectable} = this.props;
        const {selected} = this.state;
        return (
            <Marker
                key={index}
                onClick={() => this.onClick()}
                position={marker}
                icon={this.icon}
                visible={visible}
                {...this.markerProps}
            >
                {selected && first && selectable && (
                    <ProjectPopup
                        id={this.props.project.code}
                        title={this.props.project.name}
                        stageOrder={this.props.project.stage.order}
                        onClose={() => this.onClick()}
                        themes={this.props.project.themes}
                        countries={this.props.project.countriesFormatted}/>
                )}
            </Marker>
        )
    }

    onClick() {
        this.setState({...this.state, selected: !this.state.selected});
    }

    render() {
        const {
            SymbolPath,
            Marker: {MAX_ZINDEX}
        } = window['google'].maps; // eslint-disable-line dot-notation

        // Hovering the element's card highlights it like a click does.
        this.active = this.state.selected || this.props.hovered;

        this.icon = {
            path: SymbolPath.CIRCLE,
            scale: this.active ? ACTION_DOT_ACTIVE_RADIUS : ACTION_DOT_RADIUS,
            fillColor: ACTION_DOT_COLOR,
            fillOpacity: 1,
            strokeColor: this.active ? NAVY : '#FFFFFF',
            strokeWeight: 2
        };
        this.markerProps = this.active ? {zIndex: MAX_ZINDEX + 1} : {};

        return (
            <>
                {this.state.polylines.length > 0 && this.state.polylines.map(this.renderPolylines)}
                {this.state.polygons.length > 0 && this.state.polygons.map(this.renderPolygons)}
                {this.state.markers.length > 0 && this.state.markers.map(this.renderMarkers)}
                {this.state.multi_polygons.length > 0 && this.state.multi_polygons.map(this.renderPolygons)}
            </>
        );
    }

}

export default ProjectMarker;

